import {defineConfig, DocumentActionComponent, PortableTextPluginsProps} from 'sanity'
import type {Container} from '@portabletext/editor'
import {structureTool} from 'sanity/structure'
import {visionTool} from '@sanity/vision'
import {table} from '@sanity/table'
import {codeInput} from '@sanity/code-input'
import {colorInput} from '@sanity/color-input'
import {media} from 'sanity-plugin-media'
import {muxInput} from 'sanity-plugin-mux-input'
import {schemaTypes} from './schemaTypes'
import {structure, getDefaultDocumentNode} from './structure/deskStructure'
import {createAutoVersionPublishAction} from './actions/autoVersionAction'
import {createSetTireSizeTitleAndPublishAction} from './actions/setTireSizeTitleAndPublishAction'
import React from 'react'
import {buildThemeFromUrl} from '@sanity/themer-legacy'

const theme = buildThemeFromUrl('https://themer.sanity.build/api/hues?preset=retro-colonial')

// Get environment variables with defaults
const projectId = process.env.SANITY_STUDIO_PROJECT_ID || 'c8ihu5xk'
const studioEnv = process.env.SANITY_STUDIO_ENV || 'dev'

console.log('[Sanity Config] SANITY_STUDIO_ENV:', process.env.SANITY_STUDIO_ENV)
console.log('[Sanity Config] Resolved studioEnv:', studioEnv)

// AR-1823: bind @portabletext/plugin-table to our own type names, since the
// legacy @sanity/table plugin already owns `table`.
//
// Written as plain literals rather than through `defineContainer()`, whose entire
// implementation is `(config) => ({kind: 'container', ...config})`.
//
// The import must stay type-only. Only `sanity` and `@sanity/vision` are
// externalised to sanity-cdn, so a runtime import here bundles a SECOND copy of
// the editor that the Studio then loads twice over. Measured both ways: with the
// runtime import, `dist/` is 40M and two chunks contain editor-internal strings
// ("Circular dependency detected in priorities"); with the type-only import,
// 37M and zero. The package does not tree-shake.
const richTableContainers: {table: Container; row: Container; cell: Container} = {
  table: {kind: 'container', type: 'richTable', arrayField: 'rows'},
  row: {kind: 'container', type: 'richTableRow', arrayField: 'cells'},
  cell: {kind: 'container', type: 'richTableCell', arrayField: 'content'},
}

const sharedConfig = {
  projectId,
  icon: () =>
    React.createElement('img', {
      src: '/static/apex-icon.png',
      alt: 'Apex Wheels',
      style: {width: '100%', height: '100%', objectFit: 'contain'},
    }),

  plugins: [
    structureTool({
      structure,
      defaultDocumentNode: getDefaultDocumentNode,
    }),
    // Only include vision tool in development
    visionTool(),
    table(),
    codeInput(),
    colorInput(),
    media(),
    muxInput(),
  ],

  // AR-1823: turn on the Studio's built-in Portable Text table editor.
  // Enabled globally rather than per field because `richTextContent` and
  // `articleContent` are shared `of:` arrays reused across 15 fields, so a
  // per-field opt-in would mean editing every one of them. The table controls
  // only surface where `richTable` is listed in the field's `of`, so this stays
  // inert in the restricted rich-text configs.
  form: {
    components: {
      portableText: {
        plugins: (props: PortableTextPluginsProps) =>
          props.renderDefault({
            ...props,
            plugins: {
              ...props.plugins,
              table: {enabled: true, containers: richTableContainers},
            },
          }),
      },
    },
  },

  schema: {
    types: schemaTypes,
  },

  document: {
    actions: (prev: DocumentActionComponent[], context: {schemaType: string}) => {
      // For aiGenerationRules documents, wrap the publish action with auto-version
      if (context.schemaType === 'aiGenerationRules') {
        return prev.map((originalAction) => {
          // Find the publish action and wrap it
          if (originalAction.action === 'publish') {
            return createAutoVersionPublishAction(originalAction)
          }
          return originalAction
        })
      }
      // For tireSize, wrap publish to auto-populate the title field.
      if (context.schemaType === 'tireSize') {
        return prev.map((originalAction) =>
          originalAction.action === 'publish'
            ? createSetTireSizeTitleAndPublishAction(originalAction)
            : originalAction,
        )
      }
      return prev
    },
  },

  theme,
}

// Define workspace configurations
const previewWorkspace = {
  name: 'default',
  basePath: '/preview',
  title: 'Apex Wheels - Preview',
  subtitle: 'preview.apexwheels.com',
  dataset: 'preview',
  ...sharedConfig,
  theme,
}

const developWorkspace = {
  name: 'develop',
  basePath: '/develop',
  title: 'Apex Wheels - Develop',
  subtitle: 'dev.apexwheels.com',
  dataset: 'develop',
  ...sharedConfig,
}

const productionWorkspace = {
  name: 'production',
  basePath: '/production',
  title: 'Apex Wheels - Production',
  subtitle: 'apexwheels.com',
  dataset: 'production',
  ...sharedConfig,
}

const fgStagingWorkspace = {
  name: 'fg-staging',
  basePath: '/fg-staging',
  title: 'Apex Wheels - Fitment Guides Staging',
  subtitle: 'staging.apexwheels.com',
  dataset: 'fg-staging',
  ...sharedConfig,
}

// Build workspaces array based on environment
const workspaces = (() => {
  switch (studioEnv) {
    case 'production':
      console.log('[Sanity Config] Loading workspace: Production only')
      return [productionWorkspace]
    case 'preview':
      console.log('[Sanity Config] Loading workspace: Preview only')
      return [previewWorkspace]
    case 'develop':
      console.log('[Sanity Config] Loading workspace: Develop only')
      return [developWorkspace]
    default: // 'dev' or any other value - show all workspaces (including fg-staging for local dev)
      console.log('[Sanity Config] Loading workspaces: Develop + Preview + Production + FG Staging')
      return [developWorkspace, previewWorkspace, productionWorkspace, fgStagingWorkspace]
  }
})()

export default defineConfig(workspaces)
